<template>
  <div style="width:100%;">
    <div
      v-if="cardData.key!=='placeholder'"
      :class="getCardTheme"
      class="custom-card"
    >
      <!-- 左侧主体：标题 →（说明）→ 统计数字 → 新增/调减 -->
      <div class="card-main">
        <div class="card-head">
          <div class="card-title">
            <div
              v-if="cardData.title.length<6"
              class="card-title__line"
            >{{cardData.title}}</div>
            <template v-else>
              <div
                v-for="(item, idx) in cardData.title.split('\n')"
                :key="idx"
                class="card-title__line"
              >
                {{item}}
              </div>
            </template>
          </div>

          <el-popover
            v-if="cardData.tips"
            placement="top-start"
            title=""
            width="200"
            trigger="hover"
          >

            <div
              v-if="cardData.tips.split('\n').length"
              style="padding:8px 10px;"
            >
              <div v-for="(item, idx) in cardData.tips.split('\n')" :key="idx">
                {{item}}
              </div>
            </div>
            <div
              v-else
              style="padding:8px 10px;"
            >{{ cardData.tips }}</div>
            <i
              slot="reference"
              class="el-icon-info tips-icon"
            ></i>
          </el-popover>
        </div>
        <div class="card-desc">{{ cardData.description ? cardData.description : '' }}</div>

        <div class="amount-block">
          <!-- 单金额 -->
          <template v-if="!cardData.amount_type">
            <div class="price">{{Number(cardData.amount).toLocaleString(undefined,{'minimumFractionDigits':0,'maximumFractionDigits':0})}}</div>
            <!-- 数字以下的明细：调增 / 调减 / 预计进度。
                 外面这层只负责和主数字之间的那条功能分割线，没有明细时整块不渲染，
                 分割线也就不会孤零零地留在数字下面 -->
            <div
              class="amount-details"
              v-if="cardData.amount_plus || cardData.amount_minus || cardData.key === 'invoice' || cardData.key === 'collection'"
            >
              <div
                class="price-detail"
                v-if="cardData.amount_plus"
              >
                <div class="detail-label">{{ cardData.amount_plus_action ? cardData.amount_plus_action : '' }}</div>
                <div class="is-up">+{{ formatAmount(cardData.amount_plus) }}</div>
              </div>
              <div
                class="price-detail"
                v-if="cardData.amount_minus"
              >
                <div class="detail-label">{{ cardData.amount_minus_action ? cardData.amount_minus_action : '' }}</div>
                <div class="is-down">{{ formatAmount(cardData.amount_minus) }}</div>
              </div>
              <div
                class="price-detail"
                v-if="cardData.key === 'invoice'|| cardData.key ==='collection'"
              >
                <div class="estimated_amount">
                  <span>预计{{cardData.title}}</span>
                  <span class="is-up">{{ ['开票', '收款'].includes(cardData.title) ? formatAmount(cardData.estimated_amount || 0) : cardData.estimated_amount }}</span>
                </div>
                <div class="estimated_progress">
                  <span>进度</span>
                  <span class="is-down">{{cardData.progress}}%</span>
                </div>
              </div>
            </div>
          </template>

          <!-- 双金额（业务毛利 / 净报价） -->
          <template v-else-if="cardData.amount_type === 'doubleAmount'">
            <div class="price-row">
              <div class="price">{{Number(cardData.amount).toLocaleString(undefined,{'minimumFractionDigits':0,'maximumFractionDigits':0})}}</div>
              <!-- 「盈亏平衡达成」是主数字的注脚：跟在数字右边同一行读。
                   只有卡片真的窄到放不下时才回落到下一行，绝不把主数字挤成省略号 -->
              <div v-if="cardData.title==='业务毛利'" class="break-even">盈亏平衡达成：{{ breakEven }}</div>
            </div>
            <div
              v-if="!cardData.hideTaxi&&!cardData.amountExcludingTax"
              class="tax-note"
            >（含税）</div>
            <div
              v-if="cardData.amountExcludingTax"
              class="tax-note"
            >{{Number(cardData.amountExcludingTax).toLocaleString(undefined,{'minimumFractionDigits':0,'maximumFractionDigits':0})}}（不含税）</div>

            <!-- 双金额同样带 .amount-details：分割线落在 break-even / 含税注释之下，
                 始终是「数字这块」整体的下沿 -->
            <div class="amount-breakdown amount-details">
              <div class="amount-group" v-if="cardData.title !== '净报价'">
                <div class="amount-title" v-if="cardData.amount_plus||cardData.amount_minus">{{cardData.amount_title_1}}</div>
                <div style="display:flex;">
                  <div class="detail-labels" style="margin-right:10px;">
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_plus"
                    >{{ cardData.amount_plus_action ? cardData.amount_plus_action : '' }}</div>
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_minus"
                    >{{ cardData.amount_minus_action ? cardData.amount_minus_action : '' }}</div>
                  </div>
                  <!-- 数值单独包一层 span：.price-detail-2 整体是次级灰，
                       如果把语义色加在同一个元素上会被浅底/深底那条规则压掉 -->
                  <div class="amount-value">
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_plus"
                    ><span class="is-up">+{{ formatAmount(cardData.amount_plus) }}</span></div>
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_minus"
                    ><span class="is-down">{{ formatAmount(cardData.amount_minus) }}</span></div>
                  </div>
                </div>
              </div>
              <div class="amount-group">
                <div class="amount-title" v-if="cardData.amount_plus_2||cardData.amount_minus_2">{{cardData.amount_title_2}}</div>
                <div style="display:flex;">
                  <div class="detail-labels" style="margin-right:10px;">
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_plus_2"
                    >{{ cardData.amount_plus_action_2 ? cardData.amount_plus_action_2 : '' }}</div>
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_minus_2"
                    >{{ cardData.amount_minus_action_2 ? cardData.amount_minus_action_2 : '' }}</div>
                  </div>
                  <div class="amount-value">
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_plus_2"
                    ><span class="is-up">+{{ formatAmount(cardData.amount_plus_2) }}</span></div>
                    <div
                      class="price-detail-2"
                      v-if="cardData.amount_minus_2"
                    ><span class="is-down">{{ formatAmount(cardData.amount_minus_2) }}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 右侧：年同比 / 月环比（上）+ 操作（下） -->
      <div class="card-side">
        <div class="card-trends">
          <div
            v-if="cardData.link_relative_ratio"
            class="trend-row"
            :class="cardData.link_relative_ratio.value>=0 ? 'is-up' : 'is-down'"
          >
            <span class="trend-label">{{cardData.link_relative_ratio.type}}环比</span>
            <span class="trend__value">{{cardData.link_relative_ratio.value}}%</span>
            <i
              class="trend__caret"
              :class="cardData.link_relative_ratio.value>=0 ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
            ></i>
          </div>
          <div
            v-for="(yearonyearItem, idx) in yearOnYearList"
            :key="idx"
            class="trend-row"
            :class="yearonyearItem.value>=0 ? 'is-up' : 'is-down'"
          >
            <span class="trend-label">{{yearonyearItem.type === '年' ? '年同比' : '月环比'}}</span>
            <span class="trend__value">{{yearonyearItem.value}}%</span>
            <i
              class="trend__caret"
              :class="yearonyearItem.value>=0 ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
            ></i>
          </div>
        </div>

        <!-- 「只看今年的订单」是过滤开关，「查看明细」是动作入口，属于同一层级的次级操作：
             统一成同一套按钮样式，并排贴在卡片右下角 -->
        <div class="card-actions">
          <el-button
            v-if="cardData.showOnlyThisYear"
            class="card-action"
            @click="$emit('toggleOnlyThisYear')"
          >{{cardData.onlyThisYear ? '全部':'只看今年的订单'}}</el-button>
          <el-button
            class="card-action"
            @click="$emit('showDetail')"
          >查看明细</el-button>
        </div>
      </div>
    </div>
    <div
      v-else
      style="display:flex;flex-direction:row;height:200px;"
    ></div>

  </div>
</template>

<script>
import { getProfitLossLine } from '@/api/api.js'
export default {
  name: "CustomCard",
  props: ["cardData", "filterContent"],
  computed: {
    getCardTheme () {
      return this.cardData.onlyThisYear ? 'theme-dark' : 'theme-light'
    },
    breakEven() {
      return (this.cardData.amount / this.ratio * 100).toFixed(2) + '%'
    },
    /* 同比 / 环比列表：后端没返回时是 ""，v-for 直接遍历字符串会按字符拆开，
       这里统一收敛成数组 */
    yearOnYearList () {
      return Array.isArray(this.cardData.year_on_year) ? this.cardData.year_on_year : []
    }
  },
  data() {
    return {
      ratio: 2500000,
    }
  },
  methods: {
    /* 「新增 / 调减」这两个数后端是当字符串下发的（例如 "279717.80"），
       字符串上没有 toLocaleString 的格式化重载，旧代码调用后原样返回，
       所以只有主数字有千位分隔符、明细数字没有。
       这里不经过 Number() 转换，只给整数部分插分隔符：
       既补上千位分隔符，又不会把 ".80" 裁成 ".8"、也不会把 "0" 变成 "0.00"。 */
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return ''
      const raw = String(value)
      const matched = raw.match(/^(-?)(\d+)([\s\S]*)$/)
      if (!matched) return raw
      return matched[1] + matched[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + matched[3]
    },
    getProfitLossLineData() {
      if (this.cardData.title !== '业务毛利') return 
      getProfitLossLine({
        "timerange": this.filterContent.timerange
      }).then(res => {
        this.ratio = res.data.respData
      })
    }
  },
  created () {
    this.getProfitLossLineData()
  },
}
</script>

<style scoped lang="scss">
/* ── 涨跌语义色 ──────────────────────────────────────────────
   沿用卡片原有的「红=增加、绿=减少」。中途换成过与主题蓝同饱和度的
   #E5484D / #16A34A，红色偏砖、绿色偏墨，压在白卡片上发闷、发「脏」，
   这里回到原来更正的红绿，颜色只承担语义，不参与蓝色层级。 */
$trend-up: #FF0000;
$trend-down: #00B42A;

/* 次级文字分两档灰，拉开「分组表头」和「行标签」的层次：
   主数字（近黑） > 行标签「新增 / 调减」 > 分组表头「预计开票 / 进度 / 报价」。
   两者都是灰，但行标签紧贴数值，要比表头实一档，扫读时才不会糊成一片。 */
$text-head: #8A94A6;
$text-head-dark: rgba(255, 255, 255, .62);
$text-label: #4E5A6B;
$text-label-dark: rgba(255, 255, 255, .82);
/* 卡片标题：比主数字浅一档，把主视觉让给统计数字 */
$title: #3D4759;
$title-dark: rgba(255, 255, 255, .88);

.is-up {
  color: $trend-up;
}
.is-down {
  color: $trend-down;
}

.theme-dark {
  background: #040b8b;
  color: #FFFFFF;
}
.theme-light {
  background: white;
  color: #0B1220;
}
/* 深色卡片（navy #040b8b）上把红绿提亮一档，纯红在深蓝底上会发暗 */
.theme-dark .is-up {
  color: #FF6B6B;
}
.theme-dark .is-down {
  color: #35D46F;
}
/* 极轻的双层投影，让卡片从 #f5f7fa 页面底色上"浮"起来，不再是纯白方块 */
.theme-dark,
.theme-light {
  box-shadow: 0 1px 2px rgba(9, 30, 66, 0.04), 0 6px 16px rgba(9, 30, 66, 0.06);
}

/* ── 悬停 / 聚焦反馈 ──────────────────────────────────────
   卡片本身不可聚焦也不可点，但鼠标指过来、或者焦点落进卡内的
   「只看今年的订单 / 查看明细」按钮时，这张卡就是"正在被操作的那张"，
   两种状态给同一档反馈——一圈浅蓝描边（深色卡片换成半透明白），
   和 .card-action:focus 的描边同色。
   用 inset 阴影而不是 border：这几张卡片内容已经贴到 200px 下沿附近，
   加 1px 边框会把内容整体往外顶 2px，inset 不占布局 */
.custom-card:hover,
.custom-card:focus-within {
  box-shadow: inset 0 0 0 1px #A6CEFF, 0 1px 2px rgba(9, 30, 66, 0.04), 0 6px 16px rgba(9, 30, 66, 0.06);
}
.theme-dark:hover,
.theme-dark:focus-within {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.62), 0 1px 2px rgba(9, 30, 66, 0.04), 0 6px 16px rgba(9, 30, 66, 0.06);
}

/* 次级文字在两套底色下的取值 */
.theme-light {
  .card-desc,
  .trend-label,
  .price-detail,
  .price-detail-2,
  .amount-title,
  .tax-note,
  .break-even,
  .tips-icon {
    color: $text-head;
  }
  /* 行标签「新增 / 调减」：比分组表头深一档 */
  .detail-label,
  .detail-labels .price-detail-2 {
    color: $text-label;
  }
  .card-head {
    color: $title;
  }
}
.theme-dark {
  .card-desc,
  .trend-label,
  .price-detail,
  .price-detail-2,
  .amount-title,
  .tax-note,
  .break-even,
  .tips-icon {
    color: $text-head-dark;
  }
  .detail-label,
  .detail-labels .price-detail-2 {
    color: $text-label-dark;
  }
  .card-head {
    color: $title-dark;
  }
}

/* ── 骨架：左列「标题 → 统计数字 → 新增/调减」，右列「同比/环比 + 操作」 ──
   原来数字在右、涨跌在左，视线要在卡片里横向来回跳；
   现在数字紧跟在标题下面读成一句话，涨跌这类「旁注」统一收到右侧。 */
.custom-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  height: 200px;
  padding: 20px 24px;
  border-radius: 8px;
  overflow: hidden;
  /* 和 :focus-within 的描边一起做一个很短的过渡，避免聚焦时"啪"地跳一下 */
  transition: box-shadow .15s ease;
}

.card-main {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

/* 标题行：标题 + 问号提示。
   标题收小并压深一档颜色，把主视觉让给下面的统计数字 */
.card-head {
  display: flex;
  align-items: flex-start;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}
.card-title {
  min-width: 0;
  .card-title__line {
    white-space: nowrap;
  }
}
/* 问号提示：尺寸/间距从内联样式收进类里，颜色跟次级文字统一 */
.tips-icon {
  display: flex;
  width: 16px;
  height: 16px;
  margin: 8px 4px 0 8px;
  font-size: 16px;
}
.card-desc {
  font-size: 14px;
  line-height: 20px;
}

/* 统计数字 + 新增/调减：统一挂在标题下面，读成一个信息块 */
.amount-block {
  margin-top: 8px;
  min-width: 0;
}
/* 统计数字：卡片的主视觉，字号/字重都压过标题 */
.price {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
}
/* 主数字 +「盈亏平衡达成」同排：达成率只是数字的注脚，贴着数字右边读；
   原来单独成行，中间隔了一整行行高，和数字的关联被拉断了。
   flex-wrap 只做兜底：真放不下时回落到下一行，主数字不被压缩、不会被省略号吃掉 */
.price-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 8px;
  min-width: 0;
}
/* 「盈亏平衡达成」比主数字小两档，只做注脚 */
.break-even {
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

/* 主数字区 / 明细区之间的功能分割线：把「这一项是多少」和「这个数由什么构成」分成两块。
   刻意压得很淡（白底 7% 黑、深蓝底 14% 白），只做分区，不构成第二条视觉主线，
   也不跟主数字抢注意力 */
.amount-details {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid rgba(9, 30, 66, 0.07);
}
.theme-dark .amount-details {
  border-top-color: rgba(255, 255, 255, 0.14);
}

/* 增减 / 预计 / 进度：标签走次级灰，只有数值保留红绿语义色。
   这两个维度都往「不含税收入 / 采购成本」那块分组看齐：
   1) 行距统一 16px —— 和分组表头（.amount-title）同一节奏，原来表头 16px、数值行 20px，上紧下松；
   2) 标签和数值贴在一起 —— 原来 space-between 把标签顶在卡片最左、数值顶在最右，
      中间空出一大段，一个「调增 +xxx」被拆成两个孤立的东西 */
.price-detail {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
}
/* 行标签和数值的间距，和双金额卡片里 .detail-labels 的 10px 取同一量级 */
.detail-label {
  margin-right: 12px;
}

/* 「（含税）/（不含税）」这类注释原来继承了 24px，和主数字一样大，非常抢戏；
   这里收回到 14px 次级文字 */
.tax-note {
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

/* 双金额卡片（业务毛利 / 净报价）的「新增 / 调减」两栏：
   两栏结构原来差一个间距，这里对齐成完全一致的一对 */
.amount-breakdown {
  display: flex;
  white-space: nowrap;
}
.amount-group {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 16px;
}
.amount-title {
  font-size: 14px;
  line-height: 16px;
}
.amount-value {
  display: flex;
  justify-content: end;
  flex-direction: column;
  margin-right: 16px;
}
/* 「调增 / 调减」两行同样收到 16px，和上面的分组表头对齐 */
.price-detail-2 {
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  display: flex;
  justify-content: end;
}

/* ── 右侧：同比 / 环比在上，操作按钮贴底 ── */
.card-side {
  display: flex;
  flex-direction: column;
  /* 两块都以侧栏右边缘为基准贴右：按钮右下角对齐，涨跌块右边缘对齐 */
  align-items: flex-end;
  flex: 0 0 auto;
  margin-left: 16px;
}
/* 同比 / 环比：行内标签左侧对齐（标签等宽，数值自然成列）；
   再给一个固定列宽并贴住右边缘，这样每张卡片的涨跌块左右边缘都落在同一处 */
.card-trends {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 108px;
}
/* 涨跌行：语义色挂在整行上，标签再单独拿回次级灰，
   数值和箭头都继承行颜色 */
.trend-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  .trend__value {
    margin-left: 6px;
    font-weight: 600;
  }
  /* 涨跌箭头：原来用 assets/img/up.png、down.png 两张位图（16×20 被拉伸，箭杆过细发虚）；
     换成 el-icon 的实心三角 caret，尺寸由字号控制，颜色跟随 .is-up/.is-down */
  .trend__caret {
    margin-left: 2px;
    font-size: 13px;
    line-height: 1;
  }
}

/* 卡片右下角的次级操作区，始终贴底 */
.card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

/* 「只看今年的订单」和「查看明细」共用一套按钮样式：
   白底 + 浅蓝描边 + 蓝字，规格、圆角、字重完全一致，卡片里不再出现实心蓝块 */
.card-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  min-width: 84px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 400;
  color: #2273D2;
  background: #FFFFFF;
  border: 1px solid #C9E2FF;
  border-radius: 6px;

  &:hover,
  &:focus {
    color: #2273D2;
    background: #F2F8FF;
    border-color: #A6CEFF;
  }

  &:active {
    color: #2273D2;
    background: #E6F1FF;
    border-color: #A6CEFF;
  }
}

/* 深色卡片（即「只看今年」已开启）上，浅底按钮会变成整卡最亮的一块，换成半透明白 */
.theme-dark .card-action {
  color: #FFFFFF;
  background: rgba(255, 255, 255, .12);
  border-color: rgba(255, 255, 255, .42);

  &:hover,
  &:focus {
    color: #FFFFFF;
    background: rgba(255, 255, 255, .22);
    border-color: rgba(255, 255, 255, .62);
  }

  &:active {
    color: #FFFFFF;
    background: rgba(255, 255, 255, .28);
    border-color: rgba(255, 255, 255, .62);
  }
}

/* 预计 / 进度两栏：标签上、数值下；数值比标签重一档。
   每行也用 16px，两栏合起来 32px，跟「调增 / 调减」两行等高。
   去掉原来的 min-width: 7rem：那宽度把两栏各撑到 112px，再被 space-between 顶到卡片两端，
   两个数字之间空出半张卡；现在两栏各贴住自己的内容，「预计开票 / 进度」像
   「不含税收入 / 采购成本」那样挨着排 */
.estimated_amount,
.estimated_progress {
  span {
    display: block;
    line-height: 16px;
  }
  .is-up,
  .is-down {
    font-weight: 600;
  }
}
/* 两栏之间的呼吸，太贴会糊成一句 */
.estimated_progress {
  margin-left: 28px;
}
</style>
